'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { CheckCircle2 } from 'lucide-react';

interface CountryNetworkMarketingSectionProps {
    translationNamespace: string;
}

export function CountryNetworkMarketingSection({ translationNamespace }: CountryNetworkMarketingSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const items = [
        {
            title: t('networkMarketing.item1.title'),
            description: t('networkMarketing.item1.description'),
        },
        {
            title: t('networkMarketing.item2.title'),
            description: t('networkMarketing.item2.description'),
        },
    ];

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="default"
            padding="md"
            className="bg-gray-50 dark:bg-gray-900"
        >
            <div className={`container mx-auto transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}>
                <div className="max-w-6xl mx-auto">
                    <SectionHeading
                        pill={t('networkMarketing.pill')}
                        title={t('networkMarketing.title')}
                        subtitle={t('networkMarketing.subtitle')}
                        align="center"
                        maxWidthClass="max-w-4xl"
                        isVisible={sectionRef.isVisible}
                    />

                    <div className="grid lg:grid-cols-2 gap-8 items-center mt-12">
                        <div className={`space-y-6 transition-all duration-700 ${sectionRef.isVisible
                            ? 'opacity-100 translate-x-0'
                            : 'opacity-0 -translate-x-6'
                            }`} style={{ transitionDelay: '200ms' }}>
                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    className={`flex items-start gap-4 transition-all duration-700 ${sectionRef.isVisible
                                        ? 'opacity-100 translate-x-0'
                                        : 'opacity-0 -translate-x-4'
                                        }`}
                                    style={{ transitionDelay: `${300 + index * 100}ms` }}
                                >
                                    <CheckCircle2 className="w-6 h-6 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
                                    <div>
                                        <Typography variant="h3" className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                                            {item.title}
                                        </Typography>
                                        <Typography variant="p" className="text-gray-700 dark:text-gray-300">
                                            {item.description}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={`relative transition-all duration-700 ${sectionRef.isVisible
                            ? 'opacity-100 translate-x-0 scale-100'
                            : 'opacity-0 translate-x-6 scale-95'
                            }`} style={{ transitionDelay: '400ms' }}>
                            {(() => {
                                const networkImage = t('networkMarketing.image');
                                // Ensure networkImage is a valid path (starts with / or http:// or https://)
                                // If it's a translation key (contains dots/namespace), use fallback
                                const isValidImagePath = networkImage && (
                                    networkImage.startsWith('/') || 
                                    networkImage.startsWith('http://') || 
                                    networkImage.startsWith('https://')
                                );
                                
                                if (!isValidImagePath) {

                                    return null; // Don't render image if path is invalid
                                }
                                
                                return (
                                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-lg">
                                        <Image
                                            src={networkImage}
                                            alt={t('networkMarketing.imageAlt')}
                                            width={600}
                                            height={400}
                                            className="w-full h-auto rounded-lg object-cover"
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

